export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return 'N/A'

  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' }
  return new Date(dateString).toLocaleDateString('en-US', options)
}

export function formatDateAsDayMonthYear(dateString: string | null | undefined): string {
  if (!dateString) return ''

  const trimmed = dateString.trim()
  if (!trimmed) return ''

  const dayMonthYearMatch = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (dayMonthYearMatch) {
    return trimmed
  }

  const isoDateMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/)
  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch
    return `${day}/${month}/${year}`
  }

  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) {
    return trimmed
  }

  return parsed.toLocaleDateString('en-GB')
}

// For inserting into MySQL DATE columns
export function formatForMySQL(date: Date | null | undefined): string | null {
  return date ? date.toISOString().split('T')[0] : null
}

// For inserting into MySQL DATETIME columns
export function formatDateTimeForMySQL(date: Date | null | undefined): string | null {
  return date ? date.toISOString().slice(0, 19).replace('T', ' ') : null
}

const timelineSortDays: Record<string, number> = {
  immediate: 0,
  within_1_month: 30,
  months_1_3: 90,
  months_3_6: 180,
  ongoing: 365, // place ongoing after dated/near-term buckets
}

const hiringTimelineLabels: Record<string, string> = {
  immediate: 'Hiring immediately',
  within_1_month: 'Hiring within 1 month',
  ongoing: 'Ongoing hiring',
  months_1_3: 'Hiring in 1 to 3 months',
  months_3_6: 'Hiring in 3 to 6 months',
  specific_date: 'Hiring from', // completed with date below
}

/**
 * Formats hiring timeline using enum + optional date (legacy or specific date).
 */
export function formatHiringTimeline(
  hiringTimeline?: string | null,
  timelineDate?: string | null,
  timeline?: string | null
): string {
  const dateValue = timelineDate ?? timeline ?? null

  if (hiringTimeline === 'specific_date' && dateValue) {
    return `${hiringTimelineLabels[hiringTimeline]} ${formatDate(dateValue)}`
  }

  if (hiringTimeline && hiringTimelineLabels[hiringTimeline]) {
    return hiringTimelineLabels[hiringTimeline]
  }

  if (dateValue) {
    return formatDate(dateValue)
  }

  return 'Not specified'
}

/**
 * Generates a numeric sort key for hiring timeline, mixing enums and dates.
 * Lower values sort earlier.
 */
export function getHiringTimelineSortKey(
  hiringTimeline?: string | null,
  timelineDate?: string | null,
  timeline?: string | null,
  creationDate?: string | null
): number {
  const dateValue = timelineDate ?? timeline ?? null
  const baseDate = creationDate ? new Date(creationDate).getTime() : Date.now()

  if (hiringTimeline === 'specific_date' && dateValue) {
    return new Date(dateValue).getTime()
  }

  if (hiringTimeline && timelineSortDays[hiringTimeline] !== undefined) {
    return baseDate + timelineSortDays[hiringTimeline] * 24 * 60 * 60 * 1000
  }

  if (dateValue) {
    return new Date(dateValue).getTime()
  }

  return Number.POSITIVE_INFINITY
}
